package nl.volleyplanner.web.controller.planner

import kotlinx.browser.document
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.html.dom.create
import kotlinx.html.js.div
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.PageMeta
import nl.volleyplanner.web.base.WebPageController
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.routing.PageMountContext
import nl.volleyplanner.web.base.routing.PageRouteContext
import nl.volleyplanner.web.base.routing.RouteChange
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.controller.thuisprogramma.ThuisprogrammaControls
import nl.volleyplanner.web.controller.thuisprogramma.ThuisprogrammaDataManager
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import org.w3c.dom.HTMLElement

data class ProblemEditorParameters(
    val problemSlug: String
)

class ProblemEditorController(
    override val parent: PlannerIndexController
) : WebPageController() {
    override val name = "ProblemEditorController"
    override val path = "/planning/:problem_slug/editor"
    override val requiredAuthLevel = AuthLevel.AdminUser
    override val childPages = listOf<WebPageController>(
        TaskAssignmentStatisticsController(this)
    )

    private val parametersFlow = MutableStateFlow<ProblemEditorParameters?>(null)

    override fun createRuntimeInstance() = ProblemEditorController(parent)

    override suspend fun mount(context: PageMountContext) {
        super.mount(context)

        parametersFlow
            .onEach { repaint() }
            .launchIn(CoroutineManager.RENDER_SCOPE)

        val problemSlug = context.pageRouteContext.pathParameters.getValue("problem_slug")
        parametersFlow.value = ProblemEditorParameters(problemSlug)
    }

    override suspend fun update(change: RouteChange) {
        super.update(change)
        val problemSlug = change.current.pathParameters.getValue("problem_slug")
        parametersFlow.value = ProblemEditorParameters(problemSlug)
    }

    override suspend fun loadPageMeta(pageContext: PageRouteContext): PageMeta {
        val parameters = parametersFlow.value ?: parametersFlow.first { it != null }!!
        val problem = VolleyPlannerApplication.volleyplannerApiClient.organisation
            .plannerConfiguration.getProblemBySlug(parameters.problemSlug)
        return PageMeta(problem!!.name, "/planning/${parameters.problemSlug}/editor")
    }

    override suspend fun renderContent(): HTMLElement {
        val problemSlug = parametersFlow.value?.problemSlug
        return document.create.div {
            if (problemSlug == null) {
                spinner()
            } else {
                val thuisprogrammaDataManager = ThuisprogrammaDataManager(
                    problemSlug,
                    MutableStateFlow(ThuisprogrammaControls(true, false, false, showLastPlannerResult = false)),
                    "all",
                    WebUserAndOrganisationService.selectedOrganisationState.value!!,
                    CoroutineManager.CONTROLLER_SCOPE
                )
                component(ThuisProgrammaEditorComponent(thuisprogrammaDataManager))
            }
        }
    }
}